import { BaseApi, ApiResponse } from "../utils/request";

// 分页请求参数
export interface PageRequest {
  query: {
    pageNo: number;
    pageSize: number;
    status?: string;
    sysTagId?: string;
    gameId?: string | number;
    ddCreated?: {
      start?: number;
      finish?: number;
    };
    dtCreated?: {
      start?: number;
      finish?: number;
    };
    dtDate?: {
      start?: number;
      finish?: number;
    };
  };
}

export interface BetRecordSummary {
  betCount?: number;
  betAmount?: string;
  effectBetAmount?: string;
  profitAmount?: string;
  payoutAmount?: string;
}

export interface BetRecordPageResponse<T> extends PageResponse<T> {
  summary?: BetRecordSummary;
}

export interface BetChildMenu {
  sysTagId?: string | number;
  id?: string | number;
  tagId?: string | number;
  menuId?: string | number;
  title?: string;
  name?: string;
  menuName?: string;
  menuTitle?: string;
  label?: string;
  subList?: BetChildMenu[];
  children?: BetChildMenu[];
  childMenus?: BetChildMenu[];
  menus?: BetChildMenu[];
  [key: string]: unknown;
}

export interface PlayedGame {
  id: string;
  title: string;
  sysTagId?: string;
  sysTagIds?: string[];
}

export type BetChildMenuItem = BetChildMenu | string;

const BET_CHILD_MENU_LIST_KEYS = [
  "list",
  "menus",
  "menuList",
  "children",
  "childMenus",
  "items",
  "content",
] as const;

const flattenBetChildMenus = (items: BetChildMenuItem[]): BetChildMenuItem[] => {
  const result: BetChildMenuItem[] = [];

  items.forEach(item => {
    if (typeof item === "string") {
      result.push(item);
      return;
    }

    result.push(item);

    for (const key of BET_CHILD_MENU_LIST_KEYS) {
      const nested = item[key];
      if (Array.isArray(nested) && nested.length > 0) {
        result.push(...flattenBetChildMenus(nested as BetChildMenuItem[]));
      }
    }

    if (Array.isArray(item.subList) && item.subList.length > 0) {
      result.push(...flattenBetChildMenus(item.subList));
    }
  });

  return result;
};

const extractBetChildMenuArray = (payload: unknown): BetChildMenuItem[] => {
  if (!payload) return [];
  if (Array.isArray(payload)) return payload as BetChildMenuItem[];

  if (typeof payload !== "object") return [];

  const data = payload as Record<string, unknown>;
  const candidates: unknown[] = [data.model, data.data, data.result, data];

  for (const candidate of candidates) {
    if (Array.isArray(candidate)) {
      return candidate as BetChildMenuItem[];
    }

    if (candidate && typeof candidate === "object") {
      const record = candidate as Record<string, unknown>;
      for (const key of BET_CHILD_MENU_LIST_KEYS) {
        if (Array.isArray(record[key])) {
          return record[key] as BetChildMenuItem[];
        }
      }
    }
  }

  for (const key of BET_CHILD_MENU_LIST_KEYS) {
    if (Array.isArray(data[key])) {
      return data[key] as BetChildMenuItem[];
    }
  }

  return [];
};

export const parseBetChildMenus = (
  response: ApiResponse<unknown, true>
): BetChildMenuItem[] =>
  flattenBetChildMenus(extractBetChildMenuArray(response?.data));

export const toBetChildMenuOption = (
  menu: BetChildMenuItem
): { label: string; value: string } | null => {
  if (typeof menu === "string") {
    const value = menu.trim();
    if (!value) return null;
    return { label: value, value };
  }

  const value =
    menu.sysTagId ??
    menu.id ??
    menu.tagId ??
    menu.menuId ??
    (typeof menu.value === "string" || typeof menu.value === "number"
      ? menu.value
      : undefined);

  const label =
    menu.title ??
    menu.name ??
    menu.menuName ??
    menu.menuTitle ??
    menu.label ??
    (typeof menu.text === "string" ? menu.text : undefined);

  if (value == null || value === "" || !label) {
    return null;
  }

  return {
    label: String(label),
    value: String(value),
  };
};

export const matchesPlayedGameSysTag = (
  game: PlayedGame,
  sysTagId: string
): boolean => {
  if (game.sysTagId != null && String(game.sysTagId) === sysTagId) {
    return true;
  }

  return (
    game.sysTagIds?.some(tagId => String(tagId) === sysTagId) ?? false
  );
};

// 分页数据（根据实际 API 返回格式定义）
// API 返回示例：
// {
//   "status": 0,
//   "time": 1760690853134,
//   "maxAge": 0,
//   "page": {
//     "number": 1,
//     "size": 10,
//     "numberOfElements": 0,
//     "totalPages": 0,
//     "content": [],
//     "totalElements": 0
//   }
// }
export interface PageData<T> {
  number: number; // 当前页码
  size: number; // 每页数量
  numberOfElements: number; // 当前页的元素数量
  totalPages: number; // 总页数
  content: T[]; // 数据列表
  totalElements: number; // 总元素数量
}

// 分页响应包装
export interface PageResponse<T> {
  maxAge: number;
  time: number;
  status: number;
  page: PageData<T>;
}

// 充值记录
export interface RechargeRecord {
  id: string;
  created: number;
  modified: number;
  userId: string;
  orderNo: string;
  amount: number;
  status: string; // PENDING, SUCCESS, FAILED, CANCELLED
  payType: string;
  payChannel: string;
  currencyId: string;
  currencySymbol: string;
  remark?: string;
}

// 奖金记录
export interface BonusRecord {
  id: string;
  created: number;
  modified: number;
  userId: string;
  type: string; // CHECK_IN, VIP_REWARD, REBATE, PROMOTION, etc.
  amount: number;
  status: string; // PENDING, RECEIVED, EXPIRED
  source: string;
  auditRatio: number;
  description?: string;
  expireTime?: number;
}

// 投注记录
export interface BetRecord {
  id: string;
  created: number;
  modified: number;
  userId: string;
  gameId: string;
  gameName: string;
  platformId: string;
  platformName: string;
  betAmount: number;
  winAmount: number;
  profit: number;
  status: string; // PENDING, WIN, LOSE, CANCELLED
  betTime: number;
  settleTime?: number;
  currencyId: string;
  currencySymbol: string;
  orderNo: string;
}

/**
 * Records API 类
 * 继承 BaseApi，提供记录查询相关的 API 接口
 */
export class RecordsApi extends BaseApi {
  constructor() {
    super();
  }

  /**
   * 获取充值记录分页
   * @param params 分页参数
   * @returns Promise<ApiResponse<PageResponse<RechargeRecord>, true>>
   */
  async getRechargePage(params: PageRequest): Promise<ApiResponse<any, true>> {
    return this.post<any, true>("/api/pay/recharge_page", params);
  }

  /**
   * 获取提现记录分页
   * @param params 分页参数
   * @returns Promise<ApiResponse<PageResponse<any>, true>>
   */
  async getWithdrawPage(
    params: PageRequest
  ): Promise<ApiResponse<PageResponse<any>, true>> {
    return this.post<PageResponse<any>, true>("/api/pay/withdraw_page", params);
  }

  /**
   * 获取奖金记录分页
   * @param params 分页参数
   * @returns Promise<ApiResponse<PageResponse<BonusRecord>, true>>
   */
  async getBonusPage(
    params: PageRequest
  ): Promise<ApiResponse<PageResponse<any>, true>> {
    return this.post<PageResponse<any>, true>("/api/user/bonus_page", params);
  }

  /**
   * 获取投注记录分页
   * @param params 分页参数
   * @returns Promise<ApiResponse<PageResponse<BetRecord>, true>>
   */
  async getBetRecordPage(
    params: PageRequest
  ): Promise<ApiResponse<BetRecordPageResponse<any>, true>> {
    return this.post<BetRecordPageResponse<any>, true>(
      "/api/game/bet_record_page",
      params
    );
  }

  async getBetChildMenus(params: {
    menuLevel: number;
  }): Promise<ApiResponse<any, true>> {
    return this.post<any, true>(
      "/api/user_profile/getBetChildMenus",
      params
    );
  }

  /**
   * 获取奖金审核记录
   * @param params 查询参数
   * @returns Promise<ApiResponse<any, true>>
   */
  async getBounsAuditRecord(
    params: PageRequest
  ): Promise<ApiResponse<PageResponse<any>, true>> {
    return this.post<PageResponse<any>, true>("/api/user/audit_record", params);
  }

  async getGameList(): Promise<ApiResponse<any, true>> {
    return this.post<any, true>("/api/user/user_played_games", {});
  }
}

// 导出实例和便捷方法
export const recordsApi = new RecordsApi();

export const getRechargePage = (params: any) =>
  recordsApi.getRechargePage(params);

export const getWithdrawPage = (params: PageRequest) =>
  recordsApi.getWithdrawPage(params);

export const getBonusPage = (params: PageRequest) =>
  recordsApi.getBonusPage(params);

export const getBetRecordPage = (params: PageRequest) =>
  recordsApi.getBetRecordPage(params);

export const getBounsAuditRecord = (params: PageRequest) =>
  recordsApi.getBounsAuditRecord(params);

export const getGameList = () => recordsApi.getGameList();

export const getBetChildMenus = (params: { menuLevel: number }) =>
  recordsApi.getBetChildMenus(params);
